import { naturalCompare, type NoteIndexEntry } from '@kartothek/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../api';
import { NoteLink } from '../components/NoteLink';
import { useNoteIndex, useTypes } from '../hooks';
import { t } from '../i18n';

interface Node {
  note: NoteIndexEntry;
  children: Node[];
}

/** Baum aller Orte (und allem, was über „Liegt in“ verknüpft ist). */
export function PlacesPage() {
  const { cid = '' } = useParams();
  const index = useNoteIndex(cid);
  const types = useTypes(cid);
  const hierarchy = useQuery({ queryKey: ['hierarchy', cid], queryFn: () => api.hierarchy(cid) });

  useEffect(() => {
    document.title = `${t('hierarchy.places')} · Kartothek`;
  }, []);

  const roots = useMemo(() => {
    const notes = new Map((index.data ?? []).map((n) => [n.id, n]));
    const parentOf = new Map<string, string>();
    for (const e of hierarchy.data?.edges ?? []) if (!parentOf.has(e.child)) parentOf.set(e.child, e.parent);
    const involved = new Set<string>([...parentOf.keys(), ...parentOf.values()]);
    for (const n of notes.values()) if (n.typeKey === 'ort') involved.add(n.id);

    const nodes = new Map<string, Node>();
    for (const id of involved) {
      const note = notes.get(id);
      if (note) nodes.set(id, { note, children: [] });
    }
    const roots: Node[] = [];
    for (const [id, node] of nodes) {
      // Zyklen: Knoten, deren Vorfahrenkette zu sich selbst führt, werden Wurzeln
      let p = parentOf.get(id);
      const seen = new Set([id]);
      let cyclic = false;
      while (p) {
        if (seen.has(p)) {
          cyclic = true;
          break;
        }
        seen.add(p);
        p = parentOf.get(p);
      }
      const parent = parentOf.get(id);
      const parentNode = parent ? nodes.get(parent) : undefined;
      if (parentNode && !cyclic) parentNode.children.push(node);
      else roots.push(node);
    }
    const sort = (list: Node[]) => {
      list.sort((a, b) => naturalCompare(a.note.title, b.note.title));
      list.forEach((n) => sort(n.children));
    };
    sort(roots);
    return roots;
  }, [index.data, hierarchy.data]);

  const render = (list: Node[]) => (
    <ul className="place-tree">
      {list.map((n) => (
        <li key={n.note.id}>
          <span className="type-dot" style={{ background: types.byKey.get(n.note.typeKey)?.color }} />
          <NoteLink cid={cid} noteId={n.note.id}>
            {n.note.title}
          </NoteLink>
          {n.children.length > 0 && render(n.children)}
        </li>
      ))}
    </ul>
  );

  return (
    <div className="page-narrow">
      <h1>{t('hierarchy.places')}</h1>
      <p className="muted">{t('hierarchy.placesIntro')}</p>
      {index.isLoading || hierarchy.isLoading ? (
        <p className="muted">{t('common.loading')}</p>
      ) : roots.length === 0 ? (
        <p className="muted">{t('hierarchy.empty')}</p>
      ) : (
        render(roots)
      )}
    </div>
  );
}
